import { BAR_Z_RANGE, color, stroke, testColors, type TestTone } from "@mesketch/design-tokens";
import { barPosition } from "./bar.js";

// Škála kreslená tužkou (CR-014 §4, zadání 8.5.3): mírně zvlněná grafitová osa, hranice
// středového pásma, pásmo nejistoty (interval `ci` z payloadu) se šrafou v barvě testu a bod
// inkoustem s papírovým lemem. Kreslí jen hodnoty z payloadu. Skóre se neukazuje jako číslo
// (CON-002), textový ekvivalent je `label`. Táž osa slouží ipsativnímu pruhu pořadí (bez pásma).

export type ScaleSize = "lg" | "sm";
export type ScaleState = "clear" | "almost" | "drawing";

/** Hustota šrafy podle stavu (CR-014 §4): perioda v px. */
export const HATCH_PERIOD: Readonly<Record<ScaleState, number>> = { drawing: 6, almost: 5, clear: 4 };

export interface PencilScaleProps {
  readonly z: number | null;
  readonly ci: readonly [number, number] | null;
  readonly neutral: readonly [number | null, number | null] | null;
  /** Stav odhadu, `null` ve finálním výsledku (hustota jako `clear`). */
  readonly state: ScaleState | null;
  readonly lowReliability: boolean;
  readonly size: ScaleSize;
  readonly tone: TestTone;
  readonly label: string;
}

const HEIGHT: Readonly<Record<ScaleSize, number>> = { lg: 26, sm: 20 };
const DOT: Readonly<Record<ScaleSize, number>> = { lg: 14, sm: 10 };
const RING = 3;

/** Mírně zvlněná osa v souřadnicích 0 … 1000 (natahuje se na šířku, tloušťka se nemění). */
function wave(y: number, amplitude: number, phase: number): string {
  const parts = [`M0 ${y}`];
  for (let x = 125; x <= 1000; x += 125) {
    const k = x / 125;
    parts.push(`Q${x - 62.5} ${y + (k % 2 === 0 ? amplitude : -amplitude) * phase} ${x} ${y}`);
  }
  return parts.join(" ");
}

export function PencilScale({ z, ci, neutral, state, lowReliability, size, tone, label }: PencilScaleProps) {
  const t = testColors[tone];
  const h = HEIGHT[size];
  const mid = h / 2;
  const band = Math.round(h * 0.55);
  const lo = neutral === null ? null : barPosition(neutral[0] ?? -BAR_Z_RANGE);
  const hi = neutral === null ? null : barPosition(neutral[1] ?? BAR_Z_RANGE);
  const period = HATCH_PERIOD[state ?? "clear"];
  const pattern = `mk-pencil-${tone}-${period}-${lowReliability ? "l" : "n"}`;
  return (
    <svg width="100%" height={h} role="img" aria-label={label} style={{ display: "block", overflow: "visible" }} data-part="scale" data-low-reliability={lowReliability || undefined}>
      <defs>
        <pattern id={pattern} width={period} height={period} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={period} height={period} fill={t.main} opacity={0.18} />
          <line x1={period / 2} y1="0" x2={period / 2} y2={period} stroke={t.main} strokeWidth={stroke.pencil} opacity={lowReliability ? 0.3 : 0.6} />
        </pattern>
      </defs>
      <svg x="0" y="0" width="100%" height={h} viewBox={`0 0 1000 ${h}`} preserveAspectRatio="none" aria-hidden="true">
        <path d={wave(mid, 0.6, 1)} fill="none" stroke={color.graphite} strokeWidth="1.1" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
        <path d={wave(mid + 0.8, 0.5, -1)} fill="none" stroke={color.graphite} strokeWidth="0.8" opacity={0.6} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
      </svg>
      {lo !== null && hi !== null ? (
        <g stroke={color.graphite} strokeWidth="1" strokeDasharray="2 2" data-part="zone">
          <line x1={`${lo}%`} x2={`${lo}%`} y1={mid - 5} y2={mid + 5} />
          <line x1={`${hi}%`} x2={`${hi}%`} y1={mid - 5} y2={mid + 5} />
        </g>
      ) : null}
      {ci !== null ? (
        <rect
          data-part="band"
          x={`${barPosition(ci[0])}%`}
          y={mid - band / 2}
          width={`${Math.max(0, barPosition(ci[1]) - barPosition(ci[0]))}%`}
          height={band}
          rx={band / 2}
          fill={`url(#${pattern})`}
          stroke={t.strong}
          strokeWidth="1.4"
          strokeDasharray={lowReliability ? "3 2" : undefined}
        />
      ) : null}
      {z !== null ? <circle data-part="dot" cx={`${barPosition(z)}%`} cy={mid} r={DOT[size] / 2} fill={color.ink} stroke={color.paper} strokeWidth={RING} paintOrder="stroke" /> : null}
    </svg>
  );
}
